import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Settings as SettingsIcon,
  Save,
  Bell,
  Mail,
  Clock,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  Flag,
  Link2,
  Factory,
  Printer,
  TrendingUp,
  Scale,
  UserCog,
  ChevronDown,
  ChevronRight,
  Snowflake,
  Gauge,
  Package
} from 'lucide-react';
import ProductMappingTabs from './Supervisor/ProductMappingTabs';
import SupervisorStationManagement from './Supervisor/SupervisorStationManagement';
import RegisteredPrintersSection from './Supervisor/components/RegisteredPrintersSection';
import BatchLabelPrintSection from './Supervisor/components/BatchLabelPrintSection';
import PrinterTestPanel from './Supervisor/components/PrinterTestPanel';
import RegisteredScalesSection from './Supervisor/components/RegisteredScalesSection';
import ScaleTestPanel from './Supervisor/components/ScaleTestPanel';
import IcsUserManagement from './Supervisor/IcsUserManagement';
import HteStockManagement from './Supervisor/HteStockManagement';
import LabourFactorManagement from './Supervisor/LabourFactorManagement';
import PackingMaterialSettings from './Supervisor/PackingMaterialSettings';
import { useLanguage } from '../i18n/LanguageContext';
import { usePageTitle } from '../contexts/PageTitleContext';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import featureFlags from '../config/featureFlags';
import productionService from '../services/productionService';
import { wsService } from '../services/websocket';
import DbFeatureToggles from '../components/DbFeatureToggles';
import { getPrintServiceUrl, setPrintServiceUrl } from '../services/operatorService';
import { isElevatedRole, MANAGER_ROLES } from '../constants/queryConfig';

interface SystemSettings {
  // Notification Settings
  emailNotifications: boolean;
  lowStockAlerts: boolean;
  stationOfflineAlerts: boolean;
  productionAlerts: boolean;
  dailyReports: boolean;
  
  // Security Settings
  sessionTimeout: number; // in minutes
  passwordMinLength: number;
  requireStrongPassword: boolean;
  enableTwoFactor: boolean;
  maxLoginAttempts: number;
  
  // Production Settings
  defaultTargetWeight: number;
  labelCopies: number;
  simulatePrinter: boolean;
  printerSubnet: string;
  printServiceUrl: string;
  autoAssignOperators: boolean;
  allowManualWeightEntry: boolean;
  requireQualityCheck: boolean;
  
  // System Settings
  maintenanceMode: boolean;
  debugMode: boolean;
  dataRetentionDays: number;
  autoBackup: boolean;
  backupFrequency: string; // 'daily', 'weekly', 'monthly'
}

const defaultSettings: SystemSettings = {
  emailNotifications: true,
  lowStockAlerts: true,
  stationOfflineAlerts: true,
  productionAlerts: true,
  dailyReports: false,
  
  sessionTimeout: 480,
  passwordMinLength: 8,
  requireStrongPassword: true,
  enableTwoFactor: false,
  maxLoginAttempts: 5,
  
  defaultTargetWeight: 5,
  labelCopies: 2,
  simulatePrinter: false,
  printerSubnet: '',
  printServiceUrl: '',
  autoAssignOperators: false,
  allowManualWeightEntry: true,
  requireQualityCheck: false,
  
  maintenanceMode: false,
  debugMode: false,
  dataRetentionDays: 365,
  autoBackup: true,
  backupFrequency: 'daily',
};

const Settings: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isSupervisor = user?.role === 'S' || user?.role === 'supervisor';
  const isManagerOrAdmin = isElevatedRole(user?.role);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab state - synced with URL ?tab= param
  type SettingsTab = 'notifications' | 'labelPrinting' | 'scales' | 'features' | 'productMapping' | 'stationManagement' | 'userManagement' | 'production' | 'hteStock' | 'labourFactor' | 'packingMaterials';
  const defaultTab: SettingsTab = 'stationManagement';
  const activeTab = (searchParams.get('tab') as SettingsTab) || defaultTab;

  // Tab label map for dynamic page title
  const tabTitleMap: Record<SettingsTab, string> = {
    notifications: t('admin.notifications'),
    labelPrinting: t('admin.labelPrinting'),
    scales: t('admin.scales'),
    stationManagement: t('stations.title'),
    productMapping: t('byproductMapping.productMappingTitle'),
    userManagement: t('userManagement.title'),
    features: t('admin.featureFlags') || 'Features',
    production: t('admin.productionTab'),
    hteStock: t('admin.hteStock.title'),
    labourFactor: t('labourFactor.tabTitle'),
    packingMaterials: t('admin.packingMaterials.title'),
  };
  usePageTitle(tabTitleMap[activeTab] || t('nav.settings'), t('nav.settings'));
  const setActiveTab = (tab: SettingsTab) => {
    setSearchParams({ tab }, { replace: true });
  };
  const [hasChanges, setHasChanges] = useState(false);
  const [showAdvancedPrint, setShowAdvancedPrint] = useState(false);

  // Fetch settings — loads label_copies from DB, rest stays default for now
  const { data: settings = defaultSettings, isLoading } = useQuery<SystemSettings>(
    'systemSettings',
    async () => {
      try {
        const configSettings = await productionService.getConfigSettings();
        const labelCopiesSetting = configSettings.find(s => s.setting_key === 'label_copies');
        const simulatePrinterSetting = configSettings.find(s => s.setting_key === 'simulate_printer');
        const printerSubnetSetting = configSettings.find(s => s.setting_key === 'printer_subnet');
        const printServiceUrlSetting = configSettings.find(s => s.setting_key === 'print_service_url');
        return {
          ...defaultSettings,
          labelCopies: labelCopiesSetting ? Number(labelCopiesSetting.setting_value) || 2 : 2,
          simulatePrinter: simulatePrinterSetting?.setting_value === 'true',
          printerSubnet: printerSubnetSetting?.setting_value || '',
          printServiceUrl: printServiceUrlSetting?.setting_value || '',
        };
      } catch {
        return defaultSettings;
      }
    }
  );

  const [formData, setFormData] = useState<SystemSettings>(settings);

  React.useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  // Save settings mutation — persists label_copies and printer_subnet to DB
  const saveSettingsMutation = useMutation(
    async (data: SystemSettings) => {
      await productionService.createConfigSetting('label_copies', String(data.labelCopies), 'Number of label copies to print per box');
      await productionService.createConfigSetting('simulate_printer', String(data.simulatePrinter), 'When true, bypass physical printer and simulate printing');
      await productionService.createConfigSetting('printer_subnet', data.printerSubnet || '', 'Printer network subnet');
      await productionService.createConfigSetting('print_service_url', data.printServiceUrl || '', 'Print service URL');
      // Update the runtime print URL cache immediately
      setPrintServiceUrl(data.printServiceUrl || '');
      // Push subnet + simulate flag to print-service
      try {
        const base = getPrintServiceUrl();
        const url = base ? `${base}/api/print/config` : '/api/print/config';
        const token = localStorage.getItem('authToken');
        await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ subnet: data.printerSubnet || '', simulatePrinter: data.simulatePrinter })
        });
      } catch {}
      return { success: true };
    },
    {
      onSuccess: (_, data) => {
        toast.success(t('admin.settingsSavedSuccess'));
        queryClient.invalidateQueries('systemSettings');
        setHasChanges(false);
        // Notify all connected clients that config changed
        wsService.emit('config-updated', { key: 'label_copies', value: String(data.labelCopies) });
        wsService.emit('config-updated', { key: 'simulate_printer', value: String(data.simulatePrinter) });
        wsService.emit('config-updated', { key: 'print_service_url', value: data.printServiceUrl || '' });
      },
      onError: () => {
        toast.error(t('admin.failedToSaveSettings'));
      },
    }
  );

  const handleInputChange = (field: keyof SystemSettings, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    saveSettingsMutation.mutate(formData);
  };

  const handleReset = () => {
    setFormData(settings);
    setHasChanges(false);
  };

  // --- Yield Variance Threshold (Production tab, Manager/Admin only) ---
  // Value comes entirely from backend — no hardcoded default on the frontend.
  const { data: yieldData, isLoading: yieldLoading } = useQuery(
    'yieldThresholds',
    () => productionService.getYieldThresholds(),
    { staleTime: 5 * 60 * 1000, enabled: isManagerOrAdmin }
  );
  const yieldFromServer = yieldData?.yieldThreshold ?? null;
  const toDisplay = (dec: number) => String(Math.round(dec * 10000) / 100);
  const [yieldInput, setYieldInput] = useState('');
  const [yieldDirty, setYieldDirty] = useState(false);

  React.useEffect(() => {
    if (!yieldDirty && yieldFromServer !== null) setYieldInput(toDisplay(yieldFromServer));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yieldFromServer]);

  const saveYieldMutation = useMutation(
    async (decimal: number) => {
      await productionService.updateConfigSetting('yield_threshold', String(decimal));
    },
    {
      onSuccess: () => {
        toast.success(t('admin.yieldThresholdsSaved'));
        queryClient.invalidateQueries('yieldThresholds');
        setYieldDirty(false);
      },
      onError: () => { toast.error(t('admin.yieldThresholdsSaveFailed')); },
    }
  );

  const handleYieldChange = (pctValue: string) => {
    setYieldInput(pctValue);
    setYieldDirty(true);
  };

  const yieldDecimal = parseFloat(yieldInput) / 100;
  const yieldValid = yieldInput !== '' && !isNaN(yieldDecimal) && yieldDecimal >= 0 && yieldDecimal <= 1;

  // --- Weight Mode & Tolerance (Production tab, Manager/Admin only) ---
  const { data: weightSettingsData, isLoading: weightSettingsLoading } = useQuery(
    'weightSettings',
    async () => {
      const configSettings = await productionService.getConfigSettings();
      const modeSetting = configSettings.find((s: any) => s.setting_key === 'default_weight_mode');
      const tolSetting = configSettings.find((s: any) => s.setting_key === 'weight_tolerance');
      const bagEn = configSettings.find((s: any) => s.setting_key === 'bag_tare_enabled');
      const bagWt = configSettings.find((s: any) => s.setting_key === 'bag_tare_weight');
      const caseEn = configSettings.find((s: any) => s.setting_key === 'case_tare_enabled');
      const caseWt = configSettings.find((s: any) => s.setting_key === 'case_tare_weight');
      return {
        defaultWeightMode: (modeSetting?.setting_value || 'fixed') as 'fixed' | 'catch' | 'scale',
        weightTolerance: tolSetting?.setting_value || '0.05',
        bagTareEnabled: bagEn?.setting_value === 'true',
        bagTareWeight: bagWt?.setting_value || '0.05',
        caseTareEnabled: caseEn?.setting_value === 'true',
        caseTareWeight: caseWt?.setting_value || '1.35',
      };
    },
    { staleTime: 5 * 60 * 1000, enabled: isManagerOrAdmin }
  );

  const [weightModeInput, setWeightModeInput] = useState<'fixed' | 'catch' | 'scale'>('fixed');
  const [weightModeDirty, setWeightModeDirty] = useState(false);
  const [toleranceInput, setToleranceInput] = useState('0.05');
  const [toleranceDirty, setToleranceDirty] = useState(false);
  const [bagTareEnabledInput, setBagTareEnabledInput] = useState(false);
  const [bagTareWeightInput, setBagTareWeightInput] = useState('0.05');
  const [caseTareEnabledInput, setCaseTareEnabledInput] = useState(false);
  const [caseTareWeightInput, setCaseTareWeightInput] = useState('1.35');
  const [tareDirty, setTareDirty] = useState(false);

  React.useEffect(() => {
    if (weightSettingsData) {
      if (!weightModeDirty) setWeightModeInput(weightSettingsData.defaultWeightMode);
      if (!toleranceDirty) setToleranceInput(weightSettingsData.weightTolerance);
      if (!tareDirty) {
        setBagTareEnabledInput(weightSettingsData.bagTareEnabled);
        setBagTareWeightInput(weightSettingsData.bagTareWeight);
        setCaseTareEnabledInput(weightSettingsData.caseTareEnabled);
        setCaseTareWeightInput(weightSettingsData.caseTareWeight);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weightSettingsData]);

  const saveWeightModeMutation = useMutation(
    async (mode: 'fixed' | 'catch' | 'scale') => {
      await productionService.updateConfigSetting('default_weight_mode', mode);
    },
    {
      onSuccess: (_, mode) => {
        toast.success(t('admin.weightSettingsSaved'));
        queryClient.invalidateQueries('weightSettings');
        setWeightModeDirty(false);
        wsService.emit('config-updated', { key: 'default_weight_mode', value: mode });
      },
      onError: () => { toast.error(t('admin.weightSettingsSaveFailed')); },
    }
  );

  const saveToleranceMutation = useMutation(
    async (value: string) => {
      await productionService.updateConfigSetting('weight_tolerance', value);
    },
    {
      onSuccess: (_, value) => {
        toast.success(t('admin.weightSettingsSaved'));
        queryClient.invalidateQueries('weightSettings');
        setToleranceDirty(false);
        wsService.emit('config-updated', { key: 'weight_tolerance', value });
      },
      onError: () => { toast.error(t('admin.weightSettingsSaveFailed')); },
    }
  );

  const saveTareMutation = useMutation(
    async (params: { bagEnabled: boolean; bagWeight: string; caseEnabled: boolean; caseWeight: string }) => {
      await Promise.all([
        productionService.updateConfigSetting('bag_tare_enabled', String(params.bagEnabled)),
        productionService.updateConfigSetting('bag_tare_weight', params.bagWeight),
        productionService.updateConfigSetting('case_tare_enabled', String(params.caseEnabled)),
        productionService.updateConfigSetting('case_tare_weight', params.caseWeight),
      ]);
    },
    {
      onSuccess: (_, params) => {
        toast.success(t('admin.weightSettingsSaved'));
        queryClient.invalidateQueries('weightSettings');
        setTareDirty(false);
        wsService.emit('config-updated', { key: 'bag_tare_enabled', value: String(params.bagEnabled) });
        wsService.emit('config-updated', { key: 'bag_tare_weight', value: params.bagWeight });
        wsService.emit('config-updated', { key: 'case_tare_enabled', value: String(params.caseEnabled) });
        wsService.emit('config-updated', { key: 'case_tare_weight', value: params.caseWeight });
      },
      onError: () => { toast.error(t('admin.weightSettingsSaveFailed')); },
    }
  );

  // --- Transfer-Back Source Warehouse (Production / Transfers, WCO-3061) ---
  const { data: hteWarehouses = [], isLoading: hteWarehousesLoading, isError: hteWarehousesError } = useQuery<Array<{ warehouse_code: string; description: string }>>(
    'hte-warehouses',
    () => productionService.getHteWarehouses(),
    { staleTime: 5 * 60 * 1000, enabled: isManagerOrAdmin }
  );

  const { data: transferSourceSetting, isLoading: transferSourceLoading, isError: transferSourceError } = useQuery(
    'transfer-source-warehouse-setting',
    async () => {
      const configSettings = await productionService.getConfigSettings();
      return configSettings.find((s) => s.setting_key === 'transfer_source_warehouse_code')?.setting_value || 'HTE1';
    },
    { staleTime: 30_000, enabled: isManagerOrAdmin }
  );

  const [transferSourceInput, setTransferSourceInput] = useState('HTE1');
  const [transferSourceDirty, setTransferSourceDirty] = useState(false);

  React.useEffect(() => {
    if (!transferSourceDirty && transferSourceSetting) {
      setTransferSourceInput(transferSourceSetting);
    }
  }, [transferSourceSetting, transferSourceDirty]);

  const saveTransferSourceMutation = useMutation(
    async (code: string) => {
      await productionService.updateConfigSetting('transfer_source_warehouse_code', code);
    },
    {
      onSuccess: (_, code) => {
        toast.success(t('admin.transferSourceWarehouseSaved'));
        queryClient.invalidateQueries('transfer-source-warehouse-setting');
        queryClient.invalidateQueries('systemSettings-config');
        setTransferSourceDirty(false);
        wsService.emit('config-updated', { key: 'transfer_source_warehouse_code', value: code });
      },
      onError: () => { toast.error(t('admin.transferSourceWarehouseSaveFailed')); },
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const allTabs = [
    { id: 'stationManagement' as const, label: t('stations.title'), icon: Factory },
    { id: 'productMapping' as const, label: t('byproductMapping.productMappingTitle'), icon: Link2 },
    { id: 'userManagement' as const, label: t('userManagement.title'), icon: UserCog },
    { id: 'labelPrinting' as const, label: t('admin.labelPrinting'), icon: Printer },
    { id: 'scales' as const, label: t('admin.scales'), icon: Scale },
    { id: 'production' as const, label: t('admin.productionTab'), icon: TrendingUp },
    { id: 'labourFactor' as const, label: t('labourFactor.tabTitle'), icon: Gauge },
    { id: 'hteStock' as const, label: t('admin.hteStock.title'), icon: Snowflake },
    { id: 'packingMaterials' as const, label: t('admin.packingMaterials.title'), icon: Package },
    { id: 'notifications' as const, label: t('admin.notifications'), icon: Bell },
    { id: 'features' as const, label: t('admin.featureFlags') || 'Features', icon: Flag },
  ];

  // Supervisor < Manager < Admin. Each level inherits the previous + adds more.
  const supervisorTabIds = ['stationManagement', 'labelPrinting', 'scales'];
  const managerTabIds = [...supervisorTabIds, 'productMapping', 'userManagement', 'production', 'hteStock', 'labourFactor', 'packingMaterials'];
  const isManager = MANAGER_ROLES.includes(user?.role as any);
  const tabs = isSupervisor
    ? allTabs.filter(tab => supervisorTabIds.includes(tab.id))
    : isManager
      ? allTabs.filter(tab => managerTabIds.includes(tab.id))
      : allTabs;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-end items-center">
        {hasChanges && activeTab !== 'productMapping' && activeTab !== 'stationManagement' && activeTab !== 'userManagement' && activeTab !== 'hteStock' && activeTab !== 'labourFactor' && activeTab !== 'packingMaterials' && (
          <div className="flex items-center space-x-3">
            <button
              onClick={handleReset}
              className="btn-secondary flex items-center space-x-2"
              disabled={saveSettingsMutation.isLoading}
            >
              <RefreshCw className="w-4 h-4" />
              <span>{t('admin.reset')}</span>
            </button>
            <button
              onClick={handleSave}
              className="btn-primary flex items-center space-x-2"
              disabled={saveSettingsMutation.isLoading}
            >
              <Save className="w-4 h-4" />
              <span>{saveSettingsMutation.isLoading ? t('admin.saving') : t('admin.saveChanges')}</span>
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200 overflow-x-auto scrollbar-hide">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-max">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-tour={tab.id === 'userManagement' ? 'settings-ics-users' : undefined}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                  }
                `}
              >
                <Icon className={`
                  -ml-0.5 mr-2 h-5 w-5
                  ${activeTab === tab.id ? 'text-primary-500' : 'text-secondary-400 group-hover:text-secondary-500'}
                `} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Embedded pages render outside the card (they have their own layout) */}
      {activeTab === 'productMapping' && (
        <div className="h-[calc(100vh-14rem)]">
          <ProductMappingTabs />
        </div>
      )}
      {activeTab === 'stationManagement' && (
        <SupervisorStationManagement />
      )}
      {activeTab === 'userManagement' && (
        <div className="h-[calc(100vh-14rem)]">
          <IcsUserManagement />
        </div>
      )}

      {activeTab === 'labourFactor' && isManagerOrAdmin && (
        <div className="h-[calc(100vh-14rem)]">
          <LabourFactorManagement />
        </div>
      )}

      {/* Tab Content */}
      {activeTab !== 'productMapping' && activeTab !== 'stationManagement' && activeTab !== 'userManagement' && activeTab !== 'hteStock' && activeTab !== 'labourFactor' && activeTab !== 'packingMaterials' && (
      <div className="card">
        {/* Notification Settings */}
        {activeTab === 'notifications' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">{t('admin.notificationPreferences')}</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-secondary-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-secondary-400" />
                    <div>
                      <p className="text-sm font-medium text-secondary-900 dark:text-white">{t('admin.emailNotifications')}</p>
                      <p className="text-xs text-secondary-500 dark:text-gray-400">{t('admin.emailNotificationsDesc')}</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.emailNotifications}
                      onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-secondary-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <AlertCircle className="w-5 h-5 text-secondary-400" />
                    <div>
                      <p className="text-sm font-medium text-secondary-900 dark:text-white">{t('admin.lowStockAlerts')}</p>
                      <p className="text-xs text-secondary-500 dark:text-gray-400">{t('admin.lowStockAlertsDesc')}</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.lowStockAlerts}
                      onChange={(e) => handleInputChange('lowStockAlerts', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-secondary-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <AlertCircle className="w-5 h-5 text-secondary-400" />
                    <div>
                      <p className="text-sm font-medium text-secondary-900 dark:text-white">{t('admin.stationOfflineAlerts')}</p>
                      <p className="text-xs text-secondary-500 dark:text-gray-400">{t('admin.stationOfflineAlertsDesc')}</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.stationOfflineAlerts}
                      onChange={(e) => handleInputChange('stationOfflineAlerts', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-secondary-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-secondary-400" />
                    <div>
                      <p className="text-sm font-medium text-secondary-900 dark:text-white">{t('admin.productionAlerts')}</p>
                      <p className="text-xs text-secondary-500 dark:text-gray-400">{t('admin.productionAlertsDesc')}</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.productionAlerts}
                      onChange={(e) => handleInputChange('productionAlerts', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-secondary-400" />
                    <div>
                      <p className="text-sm font-medium text-secondary-900 dark:text-white">{t('admin.dailyReports')}</p>
                      <p className="text-xs text-secondary-500 dark:text-gray-400">{t('admin.dailyReportsDesc')}</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.dailyReports}
                      onChange={(e) => handleInputChange('dailyReports', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Label Printing Settings */}
        {activeTab === 'labelPrinting' && (
          <div className="space-y-6">
            {/* Top row: Simulate + Label Copies (left) | Batch Label Print (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left column — Simulate Printer & Label Copies (managers/admins only) */}
              {isManagerOrAdmin && (
                <div className="space-y-6">
                  {/* Simulate Printer Toggle */}
                  <div className="flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-amber-800 dark:text-amber-300">
                        {t('admin.simulatePrinter')}
                      </label>
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                        {t('admin.simulatePrinterDesc')}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleInputChange('simulatePrinter', !formData.simulatePrinter)}
                      className="ml-4 flex-shrink-0"
                    >
                      {formData.simulatePrinter ? (
                        <ToggleRight className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                      ) : (
                        <ToggleLeft className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                      )}
                    </button>
                  </div>

                  {/* Label Copies */}
                  <div className="max-w-xs">
                    <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-2">
                      {t('admin.labelCopies')}
                    </label>
                    <input
                      type="number"
                      className="input-field"
                      value={formData.labelCopies || ''}
                      onChange={(e) => handleInputChange('labelCopies', e.target.value === '' ? 0 : parseInt(e.target.value))}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value);
                        if (!val || val < 1) handleInputChange('labelCopies', 1);
                        else if (val > 10) handleInputChange('labelCopies', 10);
                      }}
                      min="1"
                      max="10"
                    />
                    <p className="mt-1 text-xs text-secondary-500 dark:text-gray-400">{t('admin.labelCopiesDesc')}</p>
                  </div>
                </div>
              )}

              {/* Right column — Batch Label Print */}
              <BatchLabelPrintSection
                labelCopies={formData.labelCopies}
                simulatePrinter={formData.simulatePrinter}
              />
            </div>

            {/* Registered Printers */}
            <RegisteredPrintersSection />

            {/* Test Print Panel */}
            <PrinterTestPanel />

            {/* Advanced: Subnet + Print Service URL */}
            <div className="border-t pt-4 mt-4">
              <button
                type="button"
                onClick={() => setShowAdvancedPrint(!showAdvancedPrint)}
                className="flex items-center gap-1 text-sm text-secondary-500 dark:text-gray-400 hover:text-secondary-700 dark:hover:text-gray-300 transition-colors"
              >
                {showAdvancedPrint ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                {t('admin.advancedSettings')}
              </button>
              {showAdvancedPrint && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-2">
                      {t('admin.printerSubnet')}
                    </label>
                    <input
                      type="text"
                      className="input-field font-mono"
                      value={formData.printerSubnet || ''}
                      onChange={(e) => handleInputChange('printerSubnet', e.target.value)}
                      placeholder="192.168.1"
                    />
                    <p className="mt-1 text-xs text-secondary-500 dark:text-gray-400">{t('admin.printerSubnetDesc')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-2">
                      {t('admin.printServiceUrl')}
                    </label>
                    <input
                      type="text"
                      className="input-field font-mono"
                      value={formData.printServiceUrl || ''}
                      onChange={(e) => handleInputChange('printServiceUrl', e.target.value)}
                      placeholder="https://192.168.1.100:3010"
                    />
                    <p className="mt-1 text-xs text-secondary-500 dark:text-gray-400">{t('admin.printServiceUrlDesc')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Scales Settings */}
        {activeTab === 'scales' && (
          <div className="space-y-6">
            <RegisteredScalesSection />
            <ScaleTestPanel />
          </div>
        )}

        {/* Feature Flags Settings */}
        {activeTab === 'features' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">{t('admin.featureFlags') || 'Feature Flags'}</h3>
              <p className="text-sm text-secondary-500 dark:text-gray-400 mb-4">
                {t('admin.featureFlagsEnvDesc') || 'Feature flags are controlled via environment variables.'}
              </p>

              <div className="space-y-4">
                {/* Material Type Restriction */}
                <div className="flex items-center justify-between py-4 px-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Flag className="w-5 h-5 text-purple-500" />
                    <div>
                      <p className="text-sm font-medium text-secondary-900 dark:text-white">
                        {t('admin.materialTypeRestriction') || 'Material Type Restriction'}
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-gray-400">
                        {t('admin.materialTypeRestrictionDesc') || 'Restrict stations to specific material types (Chicken, Beef, Pork, etc.)'}
                      </p>
                      <p className="text-xs text-secondary-400 dark:text-gray-500 mt-1">
                        VITE_FEATURE_MATERIAL_TYPE_RESTRICTION
                      </p>
                    </div>
                  </div>
                  <div className={`flex-shrink-0 ${
                    featureFlags.MATERIAL_TYPE_RESTRICTION
                      ? 'text-green-600'
                      : 'text-gray-400'
                  }`}>
                    {featureFlags.MATERIAL_TYPE_RESTRICTION ? (
                      <ToggleRight className="w-10 h-10" />
                    ) : (
                      <ToggleLeft className="w-10 h-10" />
                    )}
                  </div>
                </div>

                {/* Scanner Integration */}
                <div className="flex items-center justify-between py-4 px-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Flag className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="text-sm font-medium text-secondary-900 dark:text-white">
                        {t('admin.scannerIntegration') || 'Scanner Integration'}
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-gray-400">
                        {t('admin.scannerIntegrationDesc') || 'Enable barcode scanner features for scan verification'}
                      </p>
                      <p className="text-xs text-secondary-400 dark:text-gray-500 mt-1">
                        VITE_FEATURE_SCANNER_INTEGRATION
                      </p>
                    </div>
                  </div>
                  <div className={`flex-shrink-0 ${
                    featureFlags.SCANNER_INTEGRATION
                      ? 'text-green-600'
                      : 'text-gray-400'
                  }`}>
                    {featureFlags.SCANNER_INTEGRATION ? (
                      <ToggleRight className="w-10 h-10" />
                    ) : (
                      <ToggleLeft className="w-10 h-10" />
                    )}
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {t('admin.featureFlagsEnvNote') || 'Feature flags are controlled via environment variables. To change a flag, update your .env file and restart the application.'}
                </p>
              </div>
            </div>

            {/* Database-backed feature toggles */}
            <DbFeatureToggles />
          </div>
        )}

        {/* Production Settings — Yield Variance Thresholds (Manager/Admin only) */}
        {activeTab === 'production' && isManagerOrAdmin && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-1">{t('admin.yieldVarianceThresholds')}</h3>
              <p className="text-sm text-secondary-500 dark:text-gray-400 mb-4">{t('admin.yieldVarianceThresholdsDesc')}</p>

              {yieldLoading ? (
                <div className="flex items-center justify-center h-24">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <div className="max-w-sm">
                  <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-2">
                    {t('admin.yieldThresholdLabel')}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      className="input-field pr-8"
                      value={yieldInput}
                      onChange={(e) => handleYieldChange(e.target.value)}
                      step="0.1"
                      min="0"
                      max="100"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 text-sm">%</span>
                  </div>
                  <p className="mt-1 text-xs text-secondary-500 dark:text-gray-400">{t('admin.yieldThresholdDesc')}</p>
                </div>
              )}

              {yieldDirty && (
                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    onClick={() => { if (yieldFromServer !== null) setYieldInput(toDisplay(yieldFromServer)); setYieldDirty(false); }}
                    className="btn-secondary flex items-center space-x-2"
                    disabled={saveYieldMutation.isLoading}
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>{t('admin.reset')}</span>
                  </button>
                  <button
                    onClick={() => saveYieldMutation.mutate(yieldDecimal)}
                    className="btn-primary flex items-center space-x-2"
                    disabled={saveYieldMutation.isLoading || !yieldValid}
                  >
                    <Save className="w-4 h-4" />
                    <span>{saveYieldMutation.isLoading ? t('admin.saving') : t('admin.saveChanges')}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Divider */}
            <hr className="border-secondary-200 dark:border-gray-700" />

            {/* Default Weight Mode */}
            <div>
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-1">{t('admin.defaultWeightMode')}</h3>
              <p className="text-sm text-secondary-500 dark:text-gray-400 mb-4">{t('admin.defaultWeightModeDesc')}</p>

              {weightSettingsLoading ? (
                <div className="flex items-center justify-center h-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <div className="flex items-center gap-1 bg-secondary-100 dark:bg-gray-700 rounded-lg p-1 max-w-sm">
                  {(['fixed', 'catch', 'scale'] as const).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => { setWeightModeInput(mode); setWeightModeDirty(true); }}
                      className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        weightModeInput === mode
                          ? 'bg-white dark:bg-gray-600 text-primary-700 dark:text-white shadow-sm'
                          : 'text-secondary-600 dark:text-gray-400 hover:text-secondary-900 dark:hover:text-white'
                      }`}
                    >
                      {mode === 'fixed' ? t('admin.weightModeFixed') : mode === 'catch' ? t('admin.weightModeCatch') : t('admin.weightModeScale')}
                    </button>
                  ))}
                </div>
              )}

              {weightModeDirty && (
                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    onClick={() => { if (weightSettingsData) setWeightModeInput(weightSettingsData.defaultWeightMode); setWeightModeDirty(false); }}
                    className="btn-secondary flex items-center space-x-2"
                    disabled={saveWeightModeMutation.isLoading}
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>{t('admin.reset')}</span>
                  </button>
                  <button
                    onClick={() => saveWeightModeMutation.mutate(weightModeInput)}
                    className="btn-primary flex items-center space-x-2"
                    disabled={saveWeightModeMutation.isLoading}
                  >
                    <Save className="w-4 h-4" />
                    <span>{saveWeightModeMutation.isLoading ? t('admin.saving') : t('admin.saveChanges')}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Divider */}
            <hr className="border-secondary-200 dark:border-gray-700" />

            {/* Weight Tolerance */}
            <div>
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-1">{t('admin.weightTolerance')}</h3>
              <p className="text-sm text-secondary-500 dark:text-gray-400 mb-4">{t('admin.weightToleranceDesc')}</p>

              {weightSettingsLoading ? (
                <div className="flex items-center justify-center h-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <div className="max-w-sm">
                  <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-2">
                    {t('admin.weightTolerance')}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      className="input-field pr-12"
                      value={toleranceInput}
                      onChange={(e) => { setToleranceInput(e.target.value); setToleranceDirty(true); }}
                      step="0.01"
                      min="0"
                      max="10"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 text-sm">lbs</span>
                  </div>
                </div>
              )}

              {toleranceDirty && (
                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    onClick={() => { if (weightSettingsData) setToleranceInput(weightSettingsData.weightTolerance); setToleranceDirty(false); }}
                    className="btn-secondary flex items-center space-x-2"
                    disabled={saveToleranceMutation.isLoading}
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>{t('admin.reset')}</span>
                  </button>
                  <button
                    onClick={() => saveToleranceMutation.mutate(toleranceInput)}
                    className="btn-primary flex items-center space-x-2"
                    disabled={saveToleranceMutation.isLoading || !toleranceInput || isNaN(parseFloat(toleranceInput)) || parseFloat(toleranceInput) < 0}
                  >
                    <Save className="w-4 h-4" />
                    <span>{saveToleranceMutation.isLoading ? t('admin.saving') : t('admin.saveChanges')}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Auto-Tare (only visible when weight mode is Scale) */}
            {weightModeInput === 'scale' && (
              <>
                <hr className="border-secondary-200 dark:border-gray-700" />
                <div>
                  <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-1">{t('admin.autoTareTitle')}</h3>
                  <p className="text-sm text-secondary-500 dark:text-gray-400 mb-4">{t('admin.autoTareDesc')}</p>

                  <div className="space-y-4 max-w-sm">
                    {/* Bag Tare */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-sm font-medium text-secondary-700 dark:text-gray-300">{t('admin.bagTare')}</span>
                          <p className="text-xs text-secondary-500 dark:text-gray-400">{t('admin.bagTareDesc')}</p>
                        </div>
                        <button
                          onClick={() => { setBagTareEnabledInput(!bagTareEnabledInput); setTareDirty(true); }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${bagTareEnabledInput ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${bagTareEnabledInput ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {bagTareEnabledInput && (
                        <div className="relative">
                          <input
                            type="number"
                            className="input-field pr-12"
                            value={bagTareWeightInput}
                            onChange={(e) => { setBagTareWeightInput(e.target.value); setTareDirty(true); }}
                            step="0.01"
                            min="0"
                            max="5"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 text-sm">lbs</span>
                        </div>
                      )}
                    </div>

                    {/* Case Tare */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-sm font-medium text-secondary-700 dark:text-gray-300">{t('admin.caseTare')}</span>
                          <p className="text-xs text-secondary-500 dark:text-gray-400">{t('admin.caseTareDesc')}</p>
                        </div>
                        <button
                          onClick={() => { setCaseTareEnabledInput(!caseTareEnabledInput); setTareDirty(true); }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${caseTareEnabledInput ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${caseTareEnabledInput ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {caseTareEnabledInput && (
                        <div className="relative">
                          <input
                            type="number"
                            className="input-field pr-12"
                            value={caseTareWeightInput}
                            onChange={(e) => { setCaseTareWeightInput(e.target.value); setTareDirty(true); }}
                            step="0.01"
                            min="0"
                            max="10"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 text-sm">lbs</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {tareDirty && (
                    <div className="flex items-center justify-end space-x-3 pt-4">
                      <button
                        onClick={() => {
                          if (weightSettingsData) {
                            setBagTareEnabledInput(weightSettingsData.bagTareEnabled);
                            setBagTareWeightInput(weightSettingsData.bagTareWeight);
                            setCaseTareEnabledInput(weightSettingsData.caseTareEnabled);
                            setCaseTareWeightInput(weightSettingsData.caseTareWeight);
                          }
                          setTareDirty(false);
                        }}
                        className="btn-secondary flex items-center space-x-2"
                        disabled={saveTareMutation.isLoading}
                      >
                        <RefreshCw className="w-4 h-4" />
                        <span>{t('admin.reset')}</span>
                      </button>
                      <button
                        onClick={() => saveTareMutation.mutate({ bagEnabled: bagTareEnabledInput, bagWeight: bagTareWeightInput, caseEnabled: caseTareEnabledInput, caseWeight: caseTareWeightInput })}
                        className="btn-primary flex items-center space-x-2"
                        disabled={saveTareMutation.isLoading}
                      >
                        <Save className="w-4 h-4" />
                        <span>{saveTareMutation.isLoading ? t('admin.saving') : t('admin.saveChanges')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            <hr className="border-secondary-200 dark:border-gray-700" />

            {/* Production / Transfers — transfer-back source warehouse (WCO-3061) */}
            <div>
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-1">{t('admin.productionTransfers')}</h3>
              <p className="text-sm text-secondary-500 dark:text-gray-400 mb-4">{t('admin.transferSourceWarehouseCodeDesc')}</p>

              {(transferSourceLoading || hteWarehousesLoading) ? (
                <div className="flex items-center justify-center h-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : (hteWarehousesError || transferSourceError) ? (
                <p className="text-sm text-red-600 dark:text-red-400">{t('admin.transferSourceWarehouseLoadFailed')}</p>
              ) : (
                <div className="max-w-md">
                  <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-2">
                    {t('admin.transferSourceWarehouseCode')}
                  </label>
                  <select
                    className="input-field"
                    value={transferSourceInput}
                    onChange={(e) => { setTransferSourceInput(e.target.value); setTransferSourceDirty(true); }}
                    disabled={hteWarehouses.length === 0}
                  >
                    {hteWarehouses.map((wh) => (
                      <option key={wh.warehouse_code} value={wh.warehouse_code}>
                        {wh.warehouse_code}{wh.description ? ` — ${wh.description}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {transferSourceDirty && (
                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      if (transferSourceSetting) setTransferSourceInput(transferSourceSetting);
                      setTransferSourceDirty(false);
                    }}
                    className="btn-secondary flex items-center space-x-2"
                    disabled={saveTransferSourceMutation.isLoading}
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>{t('admin.reset')}</span>
                  </button>
                  <button
                    onClick={() => saveTransferSourceMutation.mutate(transferSourceInput)}
                    className="btn-primary flex items-center space-x-2"
                    disabled={saveTransferSourceMutation.isLoading || !transferSourceInput || hteWarehouses.length === 0}
                  >
                    <Save className="w-4 h-4" />
                    <span>{saveTransferSourceMutation.isLoading ? t('admin.saving') : t('admin.saveChanges')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {/* HTE Stock (Manager/Admin only) */}
      {activeTab === 'hteStock' && isManagerOrAdmin && (
        <HteStockManagement />
      )}

      {/* Packing Materials (Manager/Admin only, WCO-2155) */}
      {activeTab === 'packingMaterials' && isManagerOrAdmin && (
        <PackingMaterialSettings />
      )}

      {/* Unsaved Changes Warning */}
      {hasChanges && activeTab !== 'productMapping' && activeTab !== 'stationManagement' && activeTab !== 'userManagement' && activeTab !== 'hteStock' && activeTab !== 'labourFactor' && activeTab !== 'packingMaterials' && (
        <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 border-l-4 border-orange-500">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-orange-500" />
            <span className="text-sm text-secondary-700 dark:text-gray-300">{t('admin.unsavedChanges')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
